import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { BankIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getLecBankAccount } from '../../api/lec-bank-accounts/get-lec-bank-account'
import { updateLecBankAccount } from '../../api/lec-bank-accounts/update-lec-bank-account'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import { LecBankAccountForm } from '../../components/pages/lec-bank-accounts/lec-bank-account-form'
import { Paths } from '../../lib/constants/path'
import { LecBankAccount } from '../../lib/types/models/lec-bank-account/lec-bank-account'
import { LecBankAccountInput } from '../../lib/types/models/lec-bank-account/lec-bank-account-input'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

const mapLecBankAccountToInput = (account: LecBankAccount): LecBankAccountInput => ({
  bankName: account.bankName,
  bankAddress: account.bankAddress,
  bankAddress2: account.bankAddress2,
  bankCity: account.bankCity,
  bankZipCode: account.bankZipCode,
  bankCountry: account.bankCountry,
  iban: account.iban,
  bic: account.bic,
  currency: account.currency,
  accountHolder: account.accountHolder,
  accountHolderAddress: account.accountHolderAddress,
  olTypes: account.olTypes
})

const LecBankAccountUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()

  if (!id || isNaN(parseInt(id)))
    return <ItemNotFound title="Identifiant de compte bancaire invalide" />

  const { data: lecBankAccount, isLoading } = useQuery({
    queryKey: ['lec-bank-account', id],
    queryFn: () => getLecBankAccount(parseInt(id))
  })

  const { mutateAsync: updateLecBankAccountMutation } = useMutation({
    mutationFn: (values: LecBankAccountInput) => updateLecBankAccount(parseInt(id), values),
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lec-bank-account', id] })
      toast.success('Compte bancaire LEC mis à jour avec succès')
      navigate(Paths.LEC_BANK_ACCOUNTS_LIST_PAGE)
    }
  })

  if (isLoading) return <LoadingScreen />
  if (!lecBankAccount) return <ItemNotFound title="Compte bancaire LEC non trouvé" />

  return (
    <div>
      <UserNavBar title={'Modifier le compte bancaire LEC'} icon={BankIcon} />
      <div className={'h-4'} />
      <Box className={'flex flex-col gap-4'}>
        <LecBankAccountForm
          defaultValues={mapLecBankAccountToInput(lecBankAccount)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.LEC_BANK_ACCOUNTS_LIST_PAGE),
            onUpdate: async (values) => {
              await updateLecBankAccountMutation(values)
            }
          })}
        />
      </Box>
    </div>
  )
}

export default LecBankAccountUpdatePage
